import { useState, useRef, useCallback } from "react";
import { Camera, X, RotateCcw, Image as ImageIcon, ChevronLeft, ChevronRight } from "lucide-react";

// multi: a single tile shows the last photo; the buttons under it add more, up to count
export const PhotoGrid = ({ title, count, photos, onAdd, onAddWithGeo, onReplace, onReplaceWithGeo, onDelete, category, skipGeo, multi }) => {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [targetSlot, setTargetSlot] = useState(null);
  const [viewPhoto, setViewPhoto] = useState(null);

  const getGeo = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  });

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || targetSlot === null) return;
    // Optional/supplementary photos (extra) never need the geoloc stamp,
    // so skip the GPS lock entirely -- no permission wait for the tech.
    const geo = skipGeo ? null : await getGeo();
    const file = files[0];
    const existingPhoto = photos?.[targetSlot];
    if (existingPhoto && existingPhoto.url) {
      if (geo?.lat != null) onReplaceWithGeo(targetSlot, file, geo);
      else onReplace(targetSlot, file);
    } else {
      if (geo?.lat != null) onAddWithGeo(file, geo, targetSlot);
      else onAdd(file, targetSlot);
    }
    setTargetSlot(null);
    e.target.value = "";
  };

  const triggerSlotAction = (slotIdx) => { setTargetSlot(slotIdx); fileRef.current?.click(); };
  const triggerCamera = (slotIdx) => { setTargetSlot(slotIdx); cameraRef.current?.click(); };

  const filledCount = (photos || []).filter(p => p && (p.url || p.preview)).length;
  const slots = Array.from({ length: count }, (_, idx) => {
    const photo = photos?.[idx];
    return photo && (photo.url || photo.preview) ? photo : null;
  });
  const filledIdx = slots.map((p, i) => (p ? i : -1)).filter(i => i >= 0);
  const lastIdx = filledIdx[filledIdx.length - 1];
  const firstEmpty = slots.findIndex(p => !p);
  const shownIdx = multi ? [lastIdx ?? 0] : slots.map((_, i) => i);
  const fullUrlOf = (photo) => photo.preview || `${process.env.REACT_APP_BACKEND_URL}${photo.url}`;
  const moveViewer = (dir) => {
    const next = filledIdx[filledIdx.indexOf(viewPhoto.idx) + dir];
    if (next !== undefined) setViewPhoto({ url: fullUrlOf(slots[next]), idx: next });
  };

  // Fallback + retry: if image fails, try server URL, then retry after delay
  const retryCount = useRef({});
  const handleImgError = useCallback((e, photo, idx) => {
    const key = `${category}-${idx}`;
    const count = retryCount.current[key] || 0;
    // First: try server URL if we were showing a blob preview
    if (photo?.url && e.target.src !== `${process.env.REACT_APP_BACKEND_URL}${photo.url}`) {
      e.target.src = `${process.env.REACT_APP_BACKEND_URL}${photo.url}`;
      return;
    }
    // Then: retry the same URL after 3s (max 2 retries)
    if (count < 2 && photo?.url) {
      retryCount.current[key] = count + 1;
      setTimeout(() => {
        if (e.target) e.target.src = `${process.env.REACT_APP_BACKEND_URL}${photo.url}?retry=${count + 1}`;
      }, 3000);
    }
  }, [category]);

  return (
    <div className="space-y-2" data-testid={`photo-grid-${category}`}>
      {viewPhoto && (
        <div className="fixed inset-0 z-[100] bg-black flex flex-col" data-testid="photo-viewer" onClick={() => setViewPhoto(null)}>
          <div className="flex items-center justify-between px-4 py-3 bg-black/80 z-10">
            <span className="text-white text-sm font-medium">{title} - {multi ? `${filledIdx.indexOf(viewPhoto.idx) + 1}/${filledIdx.length}` : `Case ${viewPhoto.idx + 1}`}</span>
            <div className="flex items-center gap-3">
              {multi && (
                <button onClick={(e) => { e.stopPropagation(); const i = viewPhoto.idx; setViewPhoto(null); triggerSlotAction(i); }} className="text-white p-1 text-sm font-medium" data-testid="replace-from-viewer">
                  Reemplazar
                </button>
              )}
              <button onClick={(e) => { e.stopPropagation(); if (window.confirm("¿Eliminar esta foto?")) { onDelete(viewPhoto.idx); setViewPhoto(null); } }} className="text-red-400 p-1 text-sm font-medium" data-testid="delete-from-viewer">
                Eliminar
              </button>
              <button onClick={() => setViewPhoto(null)} className="text-white p-1" data-testid="close-photo-viewer">
                <X size={24} />
              </button>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center overflow-auto p-2 relative" onClick={e => e.stopPropagation()}>
            <img src={viewPhoto.url} alt="" className="max-w-full max-h-full object-contain" style={{ touchAction: "pinch-zoom" }} data-testid="photo-viewer-img" />
            {multi && filledIdx.length > 1 && (
              <>
                <button onClick={() => moveViewer(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full active:scale-95" data-testid="viewer-prev"><ChevronLeft size={24} /></button>
                <button onClick={() => moveViewer(1)} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full active:scale-95" data-testid="viewer-next"><ChevronRight size={24} /></button>
              </>
            )}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
        <span className="text-xs text-slate-400">{filledCount}/{count}</span>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFiles} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFiles} />
      <div className={`grid gap-3 ${multi || count <= 1 ? 'grid-cols-1' : count <= 4 ? 'grid-cols-2' : 'grid-cols-3'}`}>
        {shownIdx.map((idx) => {
          const photo = slots[idx];
          if (photo && (photo.url || photo.preview)) {
            const photoUrl = photo.url || '';
            // Use thumbnail URL for grid display (5KB vs 300KB), full URL only for viewer
            const thumbUrl = photoUrl ? photoUrl.replace('/api/tech/photo/', '/api/tech/photo-thumb/') : '';
            const gridUrl = photo.preview || (thumbUrl ? `${process.env.REACT_APP_BACKEND_URL}${thumbUrl}` : '');
            const fullUrl = photo.preview || (photoUrl ? `${process.env.REACT_APP_BACKEND_URL}${photoUrl}` : '');
            const isUploading = !photo.url && photo.preview;
            return (
              <div key={`slot-${idx}`} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
                <img src={gridUrl} alt="" className="w-full h-full object-cover cursor-pointer" loading="lazy" onClick={() => !isUploading && setViewPhoto({ url: fullUrl, idx })} onError={(e) => handleImgError(e, photo, idx)} data-testid={`photo-${category}-${idx}`} />
                {isUploading && (
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                {!isUploading && (
                  <div className="absolute top-1.5 right-1.5">
                    <button onClick={(e) => { e.stopPropagation(); triggerSlotAction(idx); }} className="p-2 bg-white/90 rounded-full shadow-md active:scale-95">
                      <RotateCcw size={18} className="text-slate-700" />
                    </button>
                  </div>
                )}
                {!multi && <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs text-center py-1 font-medium">{idx + 1}</div>}
              </div>
            );
          } else {
            return (
              <div key={`slot-${idx}`} className="aspect-square rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1" data-testid={`add-photo-${category}-slot-${idx}`}>
                <div className="flex gap-4">
                  <button onClick={() => triggerCamera(idx)} className="p-2 rounded-full bg-blue-50 text-blue-600 active:scale-95" data-testid={`camera-${category}-${idx}`}>
                    <Camera size={22} />
                  </button>
                  <button onClick={() => triggerSlotAction(idx)} className="p-2 rounded-full bg-slate-50 text-slate-500 active:scale-95" data-testid={`gallery-${category}-${idx}`}>
                    <ImageIcon size={22} />
                  </button>
                </div>
                {!multi && <span className="text-xs text-slate-400 mt-0.5">{idx + 1}</span>}
              </div>
            );
          }
        })}
      </div>
      {multi && lastIdx !== undefined && firstEmpty >= 0 && (
        <div className="flex justify-center gap-4">
          <button onClick={() => triggerCamera(firstEmpty)} className="p-2 rounded-full bg-blue-50 text-blue-600 active:scale-95" data-testid={`camera-${category}-more`}>
            <Camera size={22} />
          </button>
          <button onClick={() => triggerSlotAction(firstEmpty)} className="p-2 rounded-full bg-slate-50 text-slate-500 active:scale-95" data-testid={`gallery-${category}-more`}>
            <ImageIcon size={22} />
          </button>
        </div>
      )}
    </div>
  );
};
